import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import IndexApp from "../../Components/IndexApp";
import AddStudentDialogTrigger, { AddStudentDialogHandle } from "../../Components/shared/AddStudentDialogTrigger";
import TeachrRepository from "../../Repositories/TeachrRepository";
import { getActiveClientBrandConfig, getActivePrestataireBrandConfig } from "../../config/environment.config";
import { VT_MONTHLY_CEILING_TTC, isVerifiedTrainer } from "../../Utils/VerifiedTrainerAmounts";
import { MAX_COURSES_PER_MONTH } from "../../Utils/monthlyQuota";
import { DV2 } from "../../App/DeclarationV2/theme";
import { euros, monthName } from "../../App/DeclarationV2/format";
import { vocab } from "../../App/DeclarationV2/vocabulary";
import { Button, useDv2Theme } from "../../App/DeclarationV2/layout/Ui";
import ClientTableV2 from "../../App/Clients/ListClientV2/ClientTableV2";
import { CLIENT_LIST_ARCHIVES_TAB, CLIENT_LIST_TAB_PARAM } from "../../App/Clients/clientDetailNavigation";

// Paramètres d'URL propres à un onglet (pagination, recherche, filtres) : effacés au changement d'onglet.
const TAB_SCOPED_PARAMS = ["page", "search", "status", "status[]", "validated"];

/**
 * « Mes élèves » repensé (capture du 31 octobre 2026) : titre et compteur,
 * bouton « Ajouter un élève », onglets Actifs / Archivés avec leur nombre,
 * tableau `ClientTableV2`, note de bas de page. Même URL et mêmes paramètres
 * que la page actuelle (`?onglet=archives`, filtres de la liste).
 */
const ClientListV2Page = () => {
    const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
    const { primary, fontFamily, text } = useDv2Theme();
    const [searchParams, setSearchParams] = useSearchParams();
    const archives = searchParams.get(CLIENT_LIST_TAB_PARAM) === CLIENT_LIST_ARCHIVES_TAB;
    const studentDialogRef = useRef<AddStudentDialogHandle>(null);
    const [counts, setCounts] = useState<{ active: number | null; archived: number | null }>({ active: null, archived: null });
    const [currentTotal, setCurrentTotal] = useState<number | null>(null);

    const clientLabel = getActiveClientBrandConfig();
    const clientsLabel = getActiveClientBrandConfig("s");
    const isVt = isVerifiedTrainer();
    const ceiling = isVt ? VT_MONTHLY_CEILING_TTC : 1980 * (1 - (parseFloat(teachr?.defaultFees ?? "0") || 0));

    // Compteurs des onglets : une page d'un élément par liste, seul le total compte.
    useEffect(() => {
        if (!teachr?.id) return;
        let cancelled = false;
        const base = { teachrId: teachr.id, page: 0, variable: 0, sort: 1, limit: 1 };
        Promise.all([
            TeachrRepository.getAllClientsForTeachr({ ...base, archive: 0 }).catch(() => null),
            TeachrRepository.getAllClientsForTeachr({ ...base, archive: 1, validated: 1 }).catch(() => null),
        ]).then(([active, archived]) => {
            if (cancelled) return;
            setCounts({ active: active?.totalItems ?? null, archived: archived?.totalItems ?? null });
        });
        return () => { cancelled = true; };
    }, [teachr?.id, currentTotal]);

    const selectTab = (toArchives: boolean) => {
        if (toArchives === archives) return;
        const next = new URLSearchParams(searchParams);
        TAB_SCOPED_PARAMS.forEach((key) => next.delete(key));
        if (toArchives) next.set(CLIENT_LIST_TAB_PARAM, CLIENT_LIST_ARCHIVES_TAB);
        else next.delete(CLIENT_LIST_TAB_PARAM);
        setSearchParams(next, { replace: true });
    };

    const tab = (label: string, count: number | null, selected: boolean, onClick: () => void) => (
        <button
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={onClick}
            style={{ fontFamily, fontSize: 15, fontWeight: 800, padding: "10px 16px", background: "none", border: "none", borderBottom: `3px solid ${selected ? primary : "transparent"}`, color: selected ? primary : DV2.muted, cursor: "pointer", marginBottom: -1 }}
        >
            {label}{count != null ? `, ${count}` : ""}
        </button>
    );

    const subtitle = counts.active != null
        ? `${counts.active} ${counts.active > 1 ? clientsLabel : clientLabel} actif${counts.active > 1 ? "s" : ""}${counts.archived ? `, ${counts.archived} archivé${counts.archived > 1 ? "s" : ""}` : ""}`
        : `Retrouvez ici tous vos ${clientsLabel}`;

    return (
        <IndexApp
            navBar={{ index: 2 }}
            teachr={teachr}
            component={
                <main className="px-4" style={{ background: DV2.pageBg, minHeight: "calc(100vh - 64px)", fontFamily, color: text }}>
                    <AddStudentDialogTrigger ref={studentDialogRef} teachr={teachr} />
                    <div className="mx-auto w-full /max-w-6xl px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-5">
                        <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div>
                                <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>Mes {clientsLabel}</h1>
                                <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4 }}>{subtitle}</div>
                            </div>
                            <Button label={`+ Ajouter un ${clientLabel}`} onClick={() => studentDialogRef.current?.open()} />
                        </header>

                        <div role="tablist" aria-label={`Listes des ${clientsLabel}`} className="flex gap-2" style={{ borderBottom: `1px solid ${DV2.border}` }}>
                            {tab("Actifs", counts.active, !archives, () => selectTab(false))}
                            {tab("Archivés", counts.archived, archives, () => selectTab(true))}
                        </div>

                        {teachr?.id ? (
                            archives
                                ? <ClientTableV2 key="archives" archive={1} validated={1} onTotal={setCurrentTotal} />
                                : <ClientTableV2 key="actifs" onTotal={setCurrentTotal} />
                        ) : null}

                        <p style={{ fontSize: 12, color: DV2.muted, margin: 0, lineHeight: 1.5 }}>
                            Heures, montant et {vocab.courses()} sont ceux du mois en cours, tous {getActivePrestataireBrandConfig("s")} confondus, « par vous » précise votre part.
                            Le plafond de l'avance immédiate est de {MAX_COURSES_PER_MONTH} {vocab.courses()} et {euros(ceiling)} {isVt ? "TTC" : ""} par {clientLabel} et par mois{isVt ? "" : ` (${monthName(moment())} compris)`}.
                        </p>
                    </div>
                </main>
            }
        />
    );
};

export default ClientListV2Page;
